// =====================================================
// SELLER REGISTRATION TYPES
// =====================================================

import { SELLER_TRANSLATIONS } from '@/constants/seller-registration.translations';

export type SellerTranslations = typeof SELLER_TRANSLATIONS.en;

export interface SellerFormData {
  // Navigation
  currentStep: number; // 1-3

  // Step 1: Account & Contact
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  howHeard: string[];

  // Step 2: Business Profile
  companyName: string;
  industry: string;
  yearEstablished: string;
  
  // Jurisdictions
  jurisdictionsCanada: string[];
  jurisdictionsUSA: string[];
  jurisdictionsEurope: string[];
  jurisdictionsAustralia: string[];

  // Company Address
  addressSearch: string;
  companyStreet: string;
  companyCity: string;
  companyProvince: string;
  companyZipCode: string;

  // Financials
  annualRevenue: number;
  ebitda: number;
  askingPrice: number;

  // Pre-admission criteria
  positiveCashFlow: 'yes' | 'no' | '';
  doc3YearsPL: boolean;
  doc3YearsTax: boolean;
  docRecentBalance: boolean;
  documentsReady: 'Yes' | 'No' | 'NeedHelp' | '';

  // Additional info
  firstSale: 'Yes' | 'No' | '';
  needGuidance: 'Yes' | 'No' | '';

  // Step 3: Terms
  termsAccepted: boolean;
}

export interface SellerValidationError {
  field: string;
  message: string;
}

export interface SellerStepProps {
  formData: SellerFormData;
  updateFormData: (updates: Partial<SellerFormData>) => void;
  errors: Record<string, string>;
  clearError: (field: string) => void;
  t: SellerTranslations;
}

// Helper to get industry options from translations
export const getIndustryOptions = (t: SellerTranslations) => 
  t.industries.map((label, index) => ({
    value: label.toUpperCase().replace(/[^A-Z0-9]/g, '_').substring(0, 20),
    label,
  }));

// Helper to get how heard options from translations
export const getHowHeardOptions = (t: SellerTranslations) =>
  t.marketingSources.map((label) => ({
    value: label,
    label,
  }));

// Helper to get jurisdiction options from translations
export const getCanadaProvinces = (t: SellerTranslations) =>
  t.jurisdictionsCanadaList.map((label) => ({
    value: label.match(/\(([^)]+)\)/)?.[1] || label,
    label,
  }));

export const getUSStates = (t: SellerTranslations) =>
  t.jurisdictionsUSAList.map((label) => ({
    value: label.match(/\(([^)]+)\)/)?.[1] || label,
    label,
  }));

export const getEuropeCountries = (t: SellerTranslations) =>
  t.jurisdictionsEuropeList.map((label) => ({
    value: label.substring(0, 2).toUpperCase(),
    label,
  }));

export const getAustraliaStates = (t: SellerTranslations) =>
  t.jurisdictionsAustraliaList.map((label) => ({
    value: label.match(/\(([^)]+)\)/)?.[1] || label,
    label,
  }));
